import React from 'react';
import type {PluginListenerHandle} from '@capacitor/core';
import {KeePlayingCast} from './index';
import type {CastReceiverMessage, MessageEvent, SessionStateChangedEvent} from './definitions';
import {useCastDispatch} from '../../store/slices/cast/castSlice';
import {setSession, setValue as setCastValue} from '../../store/slices/cast/reducers';
import {usePlayerSelector, usePlayerDispatch} from '../../store/slices/player/playerSlice';
import {updateStatus} from '../../store/slices/player/actions';
import {PlayerEventBus} from '../../components/core/player/playerEventBus';
import {track} from '../../utils/telemetry';

/**
 * Single mount-point that bridges the native cast plugin to Redux + PlayerEventBus.
 * Mount once at the Player level — multiple instances would cause duplicate listeners
 * and duplicate `load` messages on the receiver.
 */
export const CastBridge: React.FC = () => {
    const castDispatch = useCastDispatch();
    const playerDispatch = usePlayerDispatch();
    const {nowPlaying} = usePlayerSelector(['nowPlaying']);

    // Latest nowPlaying captured via ref so the session-start handler can read
    // the current song without re-binding listeners on every Redux change.
    const nowPlayingRef = React.useRef(nowPlaying);
    React.useEffect(() => {
        nowPlayingRef.current = nowPlaying;
    }, [nowPlaying]);

    // Track whether we're currently casting via ref — the PlayerEventBus
    // forwarders need a synchronous read that doesn't rebind on every change.
    const isCastingRef = React.useRef(false);

    // Last position reported by the receiver. Used to seed the local iframe
    // when the cast session ends so playback resumes where the TV left off
    // instead of jumping back to 0.
    const lastPositionRef = React.useRef(0);

    const sendMessage = React.useCallback((message: CastReceiverMessage) => {
        KeePlayingCast.sendMessage({message}).catch(() => {
            // Send can fail if the session ended between dispatch and call.
            // The session-state listener will reconcile state shortly.
        });
    }, []);

    const sendLoadForCurrent = React.useCallback((startSeconds: number = 0) => {
        const np = nowPlayingRef.current;
        if (!np) return;
        sendMessage({
            type: 'load',
            videoId: np.song.id,
            title: np.song.name,
            artist: np.song.artist,
            artwork: np.song.img,
            startSeconds,
        });
    }, [sendMessage]);

    // Initial available + state probe on mount
    React.useEffect(() => {
        KeePlayingCast.isAvailable()
            .then(({available}) => castDispatch(setCastValue({key: 'available', value: available})))
            .catch(() => {});

        KeePlayingCast.getSessionState()
            .then(({state, device}) => {
                isCastingRef.current = state === 'started';
                castDispatch(setSession({state, deviceName: device?.name}));
            })
            .catch(() => {});
    }, [castDispatch]);

    // Session state listener — drives Redux + telemetry + initial load on connect
    React.useEffect(() => {
        let handle: PluginListenerHandle | undefined;
        let cancelled = false;
        let lastTrackedState: 'started' | 'ended' | 'failed' | null = null;

        KeePlayingCast.addListener('sessionStateChanged', (event: SessionStateChangedEvent) => {
            try {
                const wasCasting = isCastingRef.current;
                isCastingRef.current = event.state === 'started';

                castDispatch(setSession({state: event.state, deviceName: event.device?.name}));

                if (event.state === 'started' && !wasCasting) {
                    if (lastTrackedState !== 'started') {
                        track('cast_session_start', {});
                        lastTrackedState = 'started';
                    }
                    // Pause the local iframe before the receiver starts playing,
                    // otherwise audio plays from both phone + TV briefly.
                    PlayerEventBus.emit('pause');
                    sendLoadForCurrent(0);
                } else if ((event.state === 'ended' || event.state === 'failed') && wasCasting) {
                    const reason = event.state === 'failed' ? 'failed' : 'user';
                    if (lastTrackedState !== event.state) {
                        track('cast_session_end', {reason});
                        lastTrackedState = event.state;
                    }
                    // Hand the receiver's last reported position back to the local
                    // iframe. Use a dedicated `castHandoff` event so MediaSource's
                    // subscriber runs unconditionally — gating on isCasting would
                    // race the React effect that updates MediaSource's ref.
                    const lastPos = lastPositionRef.current;
                    if (lastPos > 0) {
                        PlayerEventBus.emit('castHandoff', lastPos);
                    }
                    lastPositionRef.current = 0;
                }
            } catch (e) {
                // Don't let a listener exception crash the WebView.
                // eslint-disable-next-line no-console
                console.warn('[cast] sessionStateChanged handler error', e);
            }
        }).then(h => {
            if (cancelled) h.remove();
            else handle = h;
        }).catch(() => {
            // Plugin not available (web platform, plugin failed to register, etc.)
        });

        return () => {
            cancelled = true;
            handle?.remove();
        };
    }, [castDispatch, sendLoadForCurrent]);

    // Receiver → app: state + position updates
    React.useEffect(() => {
        let handle: PluginListenerHandle | undefined;
        let cancelled = false;

        KeePlayingCast.addListener('message', (event: MessageEvent) => {
            try {
                const msg = event.message;
                if (!msg || typeof msg !== 'object') return;

                switch (msg.type) {
                    case 'state': {
                        const receiverState = msg.state as string;
                        if (receiverState === 'playing') playerDispatch(updateStatus('Playing'));
                        else if (receiverState === 'paused') playerDispatch(updateStatus('Paused'));
                        else if (receiverState === 'buffering') playerDispatch(updateStatus('Buffering'));
                        else if (receiverState === 'ended') {
                            playerDispatch(updateStatus('Ended'));
                            // Hand off to the existing skip-to-next logic, same as on-phone playback.
                            PlayerEventBus.emit('next');
                        }
                        break;
                    }
                    case 'position': {
                        if (typeof msg.position === 'number') {
                            lastPositionRef.current = msg.position;
                            castDispatch(setCastValue({key: 'position', value: msg.position}));
                        }
                        break;
                    }
                    case 'video_error': {
                        // Receiver hit a YouTube error (geoblock, removed video, etc.).
                        // Mirror MediaSource's local backup-fallback logic: try the
                        // next entry in song.backups before giving up and skipping.
                        const np = nowPlayingRef.current;
                        const failedId = typeof msg.videoId === 'string' ? msg.videoId : null;
                        if (!np) {
                            PlayerEventBus.emit('next');
                            break;
                        }
                        const backups = (np.song.backups ?? []) as string[];
                        let nextId: string | undefined;
                        if (failedId && failedId === np.song.id) {
                            nextId = backups[0];
                        } else if (failedId) {
                            const idx = backups.indexOf(failedId);
                            if (idx >= 0 && idx < backups.length - 1) {
                                nextId = backups[idx + 1];
                            }
                        }
                        if (nextId) {
                            sendMessage({
                                type: 'load',
                                videoId: nextId,
                                title: np.song.name,
                                artist: np.song.artist,
                                artwork: np.song.img,
                                startSeconds: 0,
                            });
                        } else {
                            // Exhausted backups — skip to next song.
                            PlayerEventBus.emit('next');
                        }
                        break;
                    }
                    default:
                        break;
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[cast] message handler error', e);
            }
        }).then(h => {
            if (cancelled) h.remove();
            else handle = h;
        }).catch(() => {
            // Plugin not available
        });

        return () => {
            cancelled = true;
            handle?.remove();
        };
    }, [castDispatch, playerDispatch]);

    // PlayerEventBus → receiver: forward play/pause/seek while casting
    React.useEffect(() => {
        const offPlay = PlayerEventBus.subscribe('play', () => {
            if (isCastingRef.current) sendMessage({type: 'play'});
        });
        const offPause = PlayerEventBus.subscribe('pause', () => {
            if (isCastingRef.current) sendMessage({type: 'pause'});
        });
        const offSeek = PlayerEventBus.subscribe('seek', (position: number) => {
            if (isCastingRef.current) sendMessage({type: 'seek', position});
        });
        return () => {
            offPlay();
            offPause();
            offSeek();
        };
    }, [sendMessage]);

    // nowPlaying changed while casting → tell receiver to load the new track
    React.useEffect(() => {
        if (!isCastingRef.current) return;
        if (!nowPlaying) return;
        sendLoadForCurrent(0);
    }, [nowPlaying, sendLoadForCurrent]);

    return null;
};
